import { Check } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

interface StepRowProps {
  index: number;
  title: string;
  done: boolean;
  /** True if this is the currently-focused step (full body visible). */
  active: boolean;
  /**
   * Compact summary shown when the step is complete. Lets the row stay
   * informative without taking up the full open-state height.
   */
  summary?: React.ReactNode;
  children?: React.ReactNode;
}

/**
 * Visual states:
 *   active   — number chip filled (bg-neutral-900), title prominent, body visible
 *   done     — checkmark chip, title muted, optional small summary
 *   pending  — outline chip, title muted, body hidden (collapsed)
 */
export function StepRow({ index, title, done, active, summary, children }: StepRowProps) {
  return (
    <div className="border-b border-neutral-200 py-5 last:border-b-0">
      <div className="flex items-start gap-4">
        <div
          className={cn(
            'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-sm font-medium transition-colors',
            done
              ? 'border-emerald-500 bg-emerald-500 text-white'
              : active
                ? 'border-blue-200 bg-blue-50 text-blue-700'
                : 'border-neutral-200 bg-white text-neutral-400'
          )}
        >
          {done ? <Check size={14} weight="bold" /> : index}
        </div>
        <div className="flex-1 pt-0.5">
          <h3
            className={cn(
              'text-base font-medium transition-colors',
              active ? 'text-neutral-900' : 'text-neutral-500'
            )}
          >
            {title}
          </h3>
          {done && summary && (
            <div className="mt-2 text-sm text-neutral-500">{summary}</div>
          )}
          {active && children && <div className="mt-4">{children}</div>}
        </div>
      </div>
    </div>
  );
}
